/**
 * 開票狀態 Invoicing Status — REFERENCED lens (S4.3).
 *
 * Visibly read-only projection of the finance store: NO action column, every
 * row carries a `SourceStampTag` (來源:會計 + syncedAt + drill-through to the
 * Finance module, where all invoicing write-back lives).
 */

import { Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import SourceStampTag from '@/components/common/SourceStampTag';
import { useOccInvoiceStatus } from '@/hooks/useOcc';
import type { OccInvoiceStatusRow } from '@/lib/occ/selectors';
import type { ArInvoiceStatus } from '@/types/api/finance';
import { formatMoney } from '../../salesOrders/components/soFormat';
import { LensFooter, LensUnavailableNotice, ReadOnlyBanner } from '../lensShared';

const STATUS_TAG_COLOR: Record<ArInvoiceStatus, string> = {
  draft: 'default',
  open: 'blue',
  partial: 'orange',
  paid: 'green',
  void: 'red',
};

const InvoiceStatusLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();
  const { rows, syncedAt, isLoading, error, refresh, unavailable } = useOccInvoiceStatus();

  const columns: TableProps<OccInvoiceStatusRow>['columns'] = [
    {
      title: t('occ:occ.invoiceStatus.columns.invoice'),
      key: 'invoice',
      width: 160,
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{row.invoiceNumber}</span>
      ),
    },
    {
      title: t('occ:occ.invoiceStatus.columns.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 240,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('occ:occ.invoiceStatus.columns.so'),
      dataIndex: 'soNumber',
      key: 'soNumber',
      width: 160,
      render: (value: string | null) => renderDash(value),
    },
    {
      title: t('occ:occ.invoiceStatus.columns.amount'),
      key: 'amount',
      width: 140,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(row.currency, row.amount)}
        </span>
      ),
    },
    {
      title: t('occ:occ.invoiceStatus.columns.outstanding'),
      key: 'outstanding',
      width: 140,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(row.currency, row.outstanding)}
        </span>
      ),
    },
    {
      title: t('occ:occ.invoiceStatus.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: ArInvoiceStatus) => (
        <Tag color={STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`occ:occ.invoiceStatus.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('occ:occ.invoiceStatus.columns.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      render: (value: string) => renderDash(value),
    },
    {
      // Referenced contract: SourceStampTag on EVERY row, no action column.
      title: t('occ:occ.invoiceStatus.columns.source'),
      key: 'source',
      width: 200,
      render: (_: unknown, row) => (
        <SourceStampTag
          sourceLabel={t('occ:occ.invoiceStatus.sourceLabel')}
          syncedAt={row.syncedAt}
          drillLabel={t('occ:occ.invoiceStatus.drill')}
          onDrill={() => navigate('/auth/finance/ar')}
        />
      ),
    },
  ];

  return (
    <>
      <LensUnavailableNotice active={unavailable} domain="invoiceStatus" />
      <ReadOnlyBanner note={t('occ:occ.invoiceStatus.readOnlyNote')} />
      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('occ:occ.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<OccInvoiceStatusRow>
          rowKey="invoiceId"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText: <ListEmptyState title={t('occ:occ.states.noInvoices')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1270 }}
        />
      </TableContainer>
      <LensFooter syncedAt={syncedAt} />
    </>
  );
};

export default InvoiceStatusLens;
